import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css'; // Import base styles
import './styles/ImageGallery.css'; // Import custom styles for the gallery
import { RowsPhotoAlbum } from "react-photo-album";
import "react-photo-album/rows.css";
import { ImageItem } from './types';
import { capitalizeFull } from './utils/util';

// Import the Zoom plugin for mobile pinch-to-zoom support
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import { getCache, setCache } from './utils/cache';
import { api } from './utils/api';


// --- React Functional Component ---
const ImageGallery: React.FC = () => {

  const { category } = useParams();

  const galleryTitle = capitalizeFull(category);
  const [index, setIndex] = React.useState(-1);

  const [images, setImages] = useState<ImageItem[]>([]);
  const [loading, setLoading] = useState(true);


  // Fetch data on mount or category change
  useEffect(() => {
    const loadImages = async () => {
      setLoading(true);
      try {
        const cacheKey = `images:${category}`;
        const cached = getCache<ImageItem[]>(cacheKey);
        if (cached && cached.length) {
          setImages(cached);
        } else {
          const res = await api.get(`/getImagesByCategory?category=${category}`);
          if (!res.ok) {
            console.error('failed to load images by category', res.status);
            setImages([]);
          } else {
            const parsed = await res.json() as ImageItem[];
            console.log('Fetched images:', parsed);
            setImages(parsed ? parsed : []);
            // cache for 15 minutes
            setCache<ImageItem[]>(cacheKey, parsed, 15);
          }
        }
      } catch (error) {
        console.error('Failed to fetch images:', error);
      } finally {
        setLoading(false);
      }
    };
    loadImages();
  }, [category]);

  // Transform ImageItem[] into the format required by the Lightbox component (Slide[])
  const lightboxSlides = images ? images.map(img => ({
    src: img.fullImageUrl,
    alt: img.fileName,
    // Custom properties can be added here
    description: `category: ${img.category}, tags: ${img.tags?.join(', ')}`,
  })) : [];

  const photos = images ? images.map((image) => ({
    src: image.fullImageUrl,
    width: image.width || 800,  // Provide a default width if missing
    height: image.height || 600, // Provide a default height if missing
  })) : [];

  return (
    <div className="image-gallery-root">
      <header className="gallery-header">
        <div className="gallery-header-inner">
          <h1 className="gallery-title">{galleryTitle} Gallery</h1>
        </div>
      </header>

      {loading ? (
        <div className="gallery-message">Loading...</div>
      ) : images.length === 0 ? (
        <div className="gallery-message">No images found.</div>
      ) : (
        <>
          <RowsPhotoAlbum
            photos={photos}
            targetRowHeight={250}
            onClick={({ index: current }) => setIndex(current)}
          />
          <Lightbox
            open={index >= 0}
            close={() => setIndex(-1)}
            slides={lightboxSlides}
            index={index}
            plugins={[Zoom]}
          />
        </>
      )}
    </div>
  );
};

export default ImageGallery;